import React from "react";
import { Button, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { IKPIData, ITimeSpan } from "./types/agedCareDashboardTypes";
import { minutesHoursFromTimeSpan, timeStampToMinutes } from "./agedCareDashboardHelpers";
import { getAlertColor, getAlertLabelDisplay, getAlertOpacity, getAlertValue, isSMAUser } from "../helpers/dashboardHelpers";
import AlertWidget from "../helpers/AlertWidget";

interface KPIWidgetProps {
  kpiData: IKPIData;
}

const borderCellValue = "1px solid rgba(255, 255, 255, 0.17)";

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    flexWrap: "wrap",
  },
  cell: {
    border: borderCellValue,
    borderTop: 0,
    flex: "1 1 14.2857%",
    padding: "0 5px",

    "&:first-child": {
      borderLeft: 0,
    },
    "&:last-child": {
      borderRight: 0,
    },

    "@media (max-width: 1280px) and (min-width: 961px)": {
      flex: "1 1 25%",

      "&:nth-child(-n+3)": {
        flex: "1 1 33.33%",
      },
      "&:nth-child(3)": {
        borderRight: 0,
      },
      "&:nth-child(4)": {
        borderLeft: 0,
      },
    },
    "@media (max-width: 960px) and (min-width: 601px)": {
      flex: "1 1 50%",

      "&:nth-child(2n)": {
        borderRight: 0,
      },
      "&:nth-child(2n+1)": {
        borderLeft: 0,
      },
    },
    "@media (max-width: 600px)": {
      flex: "1 1 100%",
    },
  },
  labelContainer: {
    textAlign: "center",
    paddingTop: 60,
    paddingBottom: 50,
  },
  nameLabel: {
    opacity: "50%",
  },
}));

const KPIWidget: React.FC<KPIWidgetProps> = ({ kpiData }) => {
  const {
    totalCallouts,
    calloutsPerDay,
    averageAcknowledgeTime,
    averageCalloutDuration,
    averageAttendTime,
    transferredViaAmbulancePercentage,
    avgCalloutsNPSSurvey,
    userRoles,
    timeToAcknowledgeRedAlert,
    timeToAcknowledgeYellowAlert,
    timeToAttendRedAlert,
    timeToAttendYellowAlert,
    calloutsPerDayRedAlert,
    calloutsPerDayYellowAlert,
    calloutsNPSSurveyRedAlert,
    calloutsNPSSurveyYellowAlert,
  } = kpiData;

  const classes = useStyles();

  const averageAcknowledgeTimeNum = timeStampToMinutes(averageAcknowledgeTime) ?? 0;
  const timeToAcknowledgeRedAlertNum = timeStampToMinutes(timeToAcknowledgeRedAlert);
  const timeToAcknowledgeYellowAlertNum = timeStampToMinutes(timeToAcknowledgeYellowAlert);

  //console.log(''+averageAcknowledgeTimeNum + ' '+timeToAcknowledgeRedAlertNum);

  const averageAttendTimeNum = timeStampToMinutes(averageAttendTime) ?? 0;
  const timeToAttendRedAlertNum = timeStampToMinutes(timeToAttendRedAlert);
  const timeToAttendYellowAlertNum = timeStampToMinutes(timeToAttendYellowAlert);

  return (
    <div className={classes.gridContainer}>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography variant="h2">{totalCallouts}</Typography>
          <Typography variant="h3" className={classes.nameLabel}>
            Total Callouts
          </Typography>
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              color: getAlertColor(userRoles, calloutsPerDay, calloutsPerDayRedAlert, calloutsPerDayYellowAlert),
            }}
          >
            {calloutsPerDay.toFixed(1)}
          </Typography>
          <Typography
            variant="h3"
            className={classes.nameLabel}
            style={{
              opacity: getAlertOpacity(userRoles, calloutsPerDay, calloutsPerDayRedAlert, calloutsPerDayYellowAlert),
              maxWidth: "3",
              color: getAlertColor(userRoles, calloutsPerDay, calloutsPerDayRedAlert, calloutsPerDayYellowAlert),
            }}
          >
            Callouts Per Day
          </Typography>
          {isSMAUser(userRoles) && (
            <AlertWidget
              userRoles={userRoles}
              currentValue={calloutsPerDay}
              redAlert={calloutsPerDayRedAlert}
              yellowAlert={calloutsPerDayYellowAlert}
              isItAbove={false}
            />
          )}
        </div>
      </div>

      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              color: getAlertColor(userRoles, averageAcknowledgeTimeNum, timeToAcknowledgeRedAlertNum, timeToAcknowledgeYellowAlertNum, true),
            }}
          >
            {minutesHoursFromTimeSpan(averageAcknowledgeTime)}
          </Typography>
          <Typography
            variant="h3"
            className={classes.nameLabel}
            style={{
              opacity: getAlertOpacity(userRoles, averageAcknowledgeTimeNum, timeToAcknowledgeRedAlertNum, timeToAcknowledgeYellowAlertNum, true),
              maxWidth: "3",
              color: getAlertColor(userRoles, averageAcknowledgeTimeNum, timeToAcknowledgeRedAlertNum, timeToAcknowledgeYellowAlertNum, true),
            }}
          >
            Average Time To Acknowledge
          </Typography>
          {isSMAUser(userRoles) && (
            <AlertWidget
              userRoles={userRoles}
              currentValue={averageAcknowledgeTimeNum}
              redAlert={timeToAcknowledgeRedAlertNum}
              yellowAlert={timeToAcknowledgeYellowAlertNum}
              isItAbove={true}
            />
          )}
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
              color: getAlertColor(userRoles, averageAttendTimeNum, timeToAttendRedAlertNum, timeToAttendYellowAlertNum, true),
            }}
          >
            {minutesHoursFromTimeSpan(averageAttendTime)}
          </Typography>
          <Typography
            variant="h3"
            className={classes.nameLabel}
            style={{
              opacity: getAlertOpacity(userRoles, averageAttendTimeNum, timeToAttendRedAlertNum, timeToAttendYellowAlertNum, true),
              maxWidth: "3",
              color: getAlertColor(userRoles, averageAttendTimeNum, timeToAttendRedAlertNum, timeToAttendYellowAlertNum, true),
            }}
          >
            Average Time To Attend
          </Typography>
          {isSMAUser(userRoles) && (
            <AlertWidget
              userRoles={userRoles}
              currentValue={averageAttendTimeNum}
              redAlert={timeToAttendRedAlertNum}
              yellowAlert={timeToAttendYellowAlertNum}
              isItAbove={true}
            />
          )}
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
            }}
          >
            {minutesHoursFromTimeSpan(averageCalloutDuration)}
          </Typography>
          <Typography variant="h3" className={classes.nameLabel}>
            Average Callout Duration
          </Typography>
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
            }}
          >
            {transferredViaAmbulancePercentage}
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: "50%",
              maxWidth: "3",
              display: "inline-block",
              color: "#fff",
            }}
          >
            %
          </Typography>
          <Typography variant="h3" className={classes.nameLabel}>
            Transferred via Ambulance
          </Typography>
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
              color: getAlertColor(userRoles, avgCalloutsNPSSurvey, calloutsNPSSurveyRedAlert, calloutsNPSSurveyYellowAlert),
            }}
          >
            {avgCalloutsNPSSurvey}
          </Typography>
          <Typography
            variant="h3"
            className={classes.nameLabel}
            style={{
              opacity: getAlertOpacity(userRoles, avgCalloutsNPSSurvey, calloutsNPSSurveyRedAlert, calloutsNPSSurveyYellowAlert),
              maxWidth: "3",
              color: getAlertColor(userRoles, avgCalloutsNPSSurvey, calloutsNPSSurveyRedAlert, calloutsNPSSurveyYellowAlert),
            }}
          >
            Average Post-callout NPS Rating
          </Typography>
          {isSMAUser(userRoles) && (
            <AlertWidget
              userRoles={userRoles}
              currentValue={avgCalloutsNPSSurvey}
              redAlert={calloutsNPSSurveyRedAlert}
              yellowAlert={calloutsNPSSurveyYellowAlert}
              isItAbove={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default KPIWidget;
